<template>
  <div class="container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <div v-if="route.query.from === 'team'" class="me-3">
          <BackButton fallback="teams-detail" :fallbackParams="{ id: route.query.teamId }" />
        </div>
        <div>
          <h1 class="mb-1">
            <font-awesome-icon icon="receipt" class="me-2" />
            Quittungen
          </h1>
          <p class="text-muted mb-0">
            Verwalte die erstellten Quittungen
          </p>
        </div>
      </div>
      <div class="d-flex gap-2 header-actions">
        <button 
          class="btn btn-outline-success" 
          @click="exportToExcel"
          :disabled="loading || receipts.length === 0"
          title="Excel Export der aktuellen Filter"
        >
          <font-awesome-icon icon="file-excel" class="me-1" />
          <span class="d-none d-sm-inline">Excel Export</span>
          <span class="d-sm-none">Excel</span>
        </button>
        <button 
          class="btn btn-outline-primary" 
          @click="exportToZip"
          :disabled="loading || receipts.length === 0"
          title="ZIP Export mit PDFs der aktuellen Filter"
        >
          <font-awesome-icon icon="file-archive" class="me-1" />
          <span class="d-none d-sm-inline">ZIP Export</span>
          <span class="d-sm-none">ZIP</span>
        </button>
        <router-link to="/receipts/create" class="btn btn-primary">
          <font-awesome-icon icon="plus" class="me-1" />
          <span class="d-none d-sm-inline">Neue Quittung</span>
          <span class="d-sm-none">Neu</span>
        </router-link>
      </div>
    </div>

    <!-- Filter -->
    <div class="card mb-4 filter-card">
      <div class="card-body filter">
        <!-- Filter Toggle Button -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <font-awesome-icon icon="filter" class="me-2" />
            Filter
          </h6>
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            @click="toggleFilters"
            :aria-expanded="filtersExpanded"
            aria-controls="filtersCollapse"
          >
            <font-awesome-icon 
              :icon="['fas', filtersExpanded ? 'chevron-up' : 'chevron-down']" 
              style="padding-right: 0.25rem;"
            />
          </button>
        </div>
        
        <div 
          :class="['collapse', { show: filtersExpanded }]" 
          id="filtersCollapse"
        >
        <!-- Search Bar -->
        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label">Suche</label>
            <div class="input-group">
              <span class="input-group-text bg-primary text-white">
                <font-awesome-icon icon="search" />
              </span>
              <input 
                v-model="filters.search" 
                type="text" 
                class="form-control" 
                placeholder="Durchsuche Titel, Empfänger, Aussteller, Betrag und alle Datenfelder..."
                @input="onSearchInput"
              >
              <button 
                v-if="filters.search" 
                @click="clearSearch" 
                class="btn btn-outline-secondary" 
                type="button"
              >
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>
        </div>
        
        <!-- Optimized Filter Layout -->
        <div class="row g-3">
          <!-- First row: Team, Status, Payment Status -->
          <div class="col-xl-4 col-lg-6 col-md-6">
            <label class="form-label">Bereich</label>
            <Multiselect
              v-model="selectedTeam"
              :options="teamOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Bereich auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Bereiche verfügbar'"
              :no-results="'Keine Bereiche gefunden'"
              @select="onTeamFilterChange"
            />
          </div>
          <div class="col-xl-4 col-lg-6 col-md-6">
            <label class="form-label">Status</label>
            <Multiselect
              v-model="selectedStatus"
              :options="statusOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Status auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Status verfügbar'"
              :no-results="'Keine Status gefunden'"
              @select="onStatusFilterChange"
            />
          </div>
          <div class="col-xl-4 col-lg-6 col-md-6">
            <label class="form-label">Bezahlstatus</label>
            <Multiselect
              v-model="selectedPaymentStatus"
              :options="paymentStatusOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Bezahlstatus auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Bezahlstatus verfügbar'"
              :no-results="'Keine Bezahlstatus gefunden'"
              @select="onPaymentStatusFilterChange"
            />
          </div>
          
          <!-- Second row: Design filter and Dates -->
          <div class="col-xl-4 col-lg-6 col-md-6">
            <label class="form-label">Design</label>
            <Multiselect
              v-model="selectedTemplate"
              :options="templateFilterOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Design auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Designs verfügbar'"
              :no-results="'Keine Designs gefunden'"
              @select="onTemplateFilterChange"
            />
          </div>
          <div class="col-xl-4 col-lg-6 col-md-6">
            <label class="form-label">Von Datum</label>
            <input v-model="filters.dateFrom" type="date" class="form-control" @change="updateUrlQuery(); loadReceipts()">
          </div>
          <div class="col-xl-4 col-lg-6 col-md-6">
            <label class="form-label">Bis Datum</label>
            <input v-model="filters.dateTo" type="date" class="form-control" @change="updateUrlQuery(); loadReceipts()">
          </div>
        </div>
        
        <!-- Team Admin Options -->
        <div v-if="(filters.teamId && filters.teamId !== 'all' && isTeamAdmin(filters.teamId)) || filters.teamId === 'all'" class="row mt-3">
          <div class="col-12">
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="showOnlyPersonalReceipts"
                :checked="filters.viewType === 'own'"
                @change="togglePersonalView"
              >
              <label class="form-check-label" for="showOnlyPersonalReceipts">
                <font-awesome-icon icon="user" class="me-1" />
                <span v-if="filters.teamId === 'all'">
                  Nur meine Quittungen anzeigen
                </span>
                <span v-else>
                  Nur meine Quittungen anzeigen
                </span>
              </label>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>



    <!-- Receipts List -->
    <div v-else-if="receipts.length > 0" class="row">
      <div v-for="receipt in receipts" :key="receipt.id" class="col-12 mb-3">
        <div class="receipt-card card shadow-sm" @click="viewReceipt(receipt.id)">
          <div class="card-body p-4">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Left Content -->
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <h6 class="card-title mb-0 fw-bold text-dark">{{ receipt.title }}</h6>
                  <span class="text-muted small">#{{ receipt.receiptNumber }}</span>
                  <div class="d-flex gap-1">
                    <span 
                      :class="[
                        'badge rounded-pill px-2 py-1',
                        receipt.status === 'draft' ? 'bg-light text-dark border' : 
                        receipt.status === 'rejected' ? 'bg-danger' :
                        receipt.status === 'canceled' ? 'bg-danger' :
                        'bg-warning'
                      ]"
                      v-if="receipt.status === 'draft' || receipt.status === 'rejected' || receipt.status === 'canceled'"
                    >
                      {{ getStatusLabel(receipt.status) }}
                    </span>
                    <span 
                      :class="[
                        'badge rounded-pill px-2 py-1',
                        receipt.payed === 'payed' ? 'bg-success' : 
                        receipt.payed === 'planed' ? 'bg-info' : 
                        'bg-warning'
                      ]"
                      v-if=" receipt.status !== 'rejected' && receipt.status !== 'canceled' && receipt?.refundActivated"
                    >
                      {{ getPaymentStatusLabel(receipt.payed) }}
                    </span>
                  </div>
                </div>
                
                <!-- Compact Details -->
                <div class="d-flex flex-wrap gap-3 text-muted small">
                  <span class="fw-bold text-primary">{{ formatAmount(receipt.amount) }} €</span>
                  <span v-if="receipt.description">{{ receipt.description }}</span>
                  <span>
                    <font-awesome-icon icon="tag" class="me-1" />
                    {{ getTemplateName(receipt) }}
                  </span>
                  <span v-if="receipt.teamId">
                    <font-awesome-icon icon="users" class="me-1" />
                    {{ receipt?.team?.name || receipt.teamName }}
                  </span>
                  <span v-else>
                    <font-awesome-icon icon="user" class="me-1" />
                    Persönlich
                  </span>
                  <span
                    class="d-flex align-items-center"
                    :title="getReceiptDateTooltip(receipt) || undefined"
                  >
                    <font-awesome-icon icon="calendar" class="me-1" />
                    {{ formatReceiptResolvedDate(receipt) }}
                    <span
                      v-if="getReceiptDateSource(receipt)"
                      class="badge bg-light text-muted border ms-2"
                    >
                      {{ getReceiptDateSource(receipt) }}
                    </span>
                  </span>
                  <span v-if="showCreatedBy(receipt)">
                    <font-awesome-icon icon="user-circle" class="me-1" />
                    {{ getCreatorName(receipt) }}
                  </span>
                  <span v-else-if="receipt.userFirstName && receipt.userLastName">
                    <font-awesome-icon icon="user-circle" class="me-1" />
                    {{ receipt.userFirstName }} {{ receipt.userLastName }}
                  </span>
                  <span v-if="receipt.taxRate && receipt.taxRate !== '0'">
                    MwSt: {{ receipt.taxRate }}%
                  </span>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="d-flex gap-2 ms-3 action-buttons" @click.stop>
                <button
                  class="btn btn-primary btn-sm"
                  @click="viewReceipt(receipt.id)"
                  title="Ansehen"
                >
                  <font-awesome-icon icon="eye" />
                </button>
                
                <button
                  v-if="receipt.pdfPath && receipt.status !== 'canceled' && receipt.status !== 'rejected'"
                  class="btn btn-outline-success btn-sm"
                  @click="downloadPDF(receipt.id)"
                  title="PDF herunterladen"
                >
                  <font-awesome-icon icon="download" />
                </button>

                <button
                  class="btn btn-outline-primary btn-sm"
                  @click="recreateReceipt(receipt)"
                  title="Quittung erneut erstellen"
                  v-if="!(!receipt.teamTemplateId && receipt.teamId)"
                >
                  <font-awesome-icon icon="redo" />
                </button>

                <button
                  v-if="receipt.status === 'draft'"
                  class="btn btn-outline-danger btn-sm"
                  @click="deleteReceipt(receipt)"
                  title="Löschen"
                >
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Search Info -->
    <div v-if="filters.search" class="alert alert-info mb-3">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <font-awesome-icon icon="search" class="me-2" />
          Suche nach: <strong>"{{ filters.search }}"</strong>
          <span v-if="pagination"> - {{ pagination.total }} Ergebnis(se) gefunden</span>
        </div>
        <button @click="clearSearch" class="btn btn-sm btn-outline-info">
          <font-awesome-icon icon="times" class="me-1" />
          Suche löschen
        </button>
      </div>
    </div>



    <!-- Empty State -->
    <div v-else-if="!loading && !error && receipts.length === 0" class="text-center my-5">
      <font-awesome-icon icon="receipt" size="3x" class="text-muted mb-3" />
      <h4 class="text-muted">Keine Quittungen gefunden</h4>
      <p class="text-muted">
        {{ filters.search ? `Keine Quittungen gefunden für "${filters.search}". Versuche andere Suchbegriffe.` :
           filters.teamId || filters.status ? 'Keine Quittungen entsprechen den gewählten Filtern.' : 
           'Du hast noch keine Quittungen erstellt.' }}
      </p>
      <router-link to="/receipts/create" class="btn btn-primary">
        <font-awesome-icon icon="plus" class="me-1" />
        Erste Quittung erstellen
      </router-link>
    </div>

    <!-- Pagination -->
    <nav v-if="!loading && !error && receipts.length > 0" class="mt-4">
      <div class="d-flex justify-content-between align-items-center">
        <!-- Left side - Count (same width as right side for balance) -->
        <div class="text-muted small" style="min-width: 150px;">
          <strong>{{ pagination?.total || receipts.length }}</strong> 
          {{ (pagination?.total || receipts.length) === 1 ? 'Quittung' : 'Quittungen' }} 
          gefunden
        </div>
        
        <!-- Center - Pagination controls -->
        <div class="d-flex align-items-center gap-3">
          <button
            v-if="pagination && pagination.totalPages > 1"
            @click="previousPage"
            :disabled="pagination.page <= 1 || loading"
            class="btn btn-outline-secondary btn-sm"
          >
            ← Vorherige
          </button>
          
          <span v-if="pagination && pagination.totalPages > 1" class="text-muted small">
            Seite {{ pagination.page }} von {{ pagination.totalPages }}
          </span>
          
          <button
            v-if="pagination && pagination.totalPages > 1"
            @click="nextPage"
            :disabled="pagination.page >= pagination.totalPages || loading"
            class="btn btn-outline-secondary btn-sm"
          >
            Nächste →
          </button>
        </div>
        
        <!-- Right side - empty for balance -->
        <div style="min-width: 150px;"></div>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { receiptsApi } from '@/api/receipts'
import { showSuccess, showError } from '@/services/notification'
import { useSmartNavigation, FallbackRoutes } from '@/services/navigation'
import { resolveReceiptDateDetails, getReceiptDateValue } from '@/utils/receiptDateUtils'
import { storeToRefs } from 'pinia'
import Swal from 'sweetalert2'
import Multiselect from 'vue-multiselect'


const components = {
  Multiselect
}

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const teamsStore = useTeamsStore()
const { teams } = storeToRefs(teamsStore)
const { goBackToTeam } = useSmartNavigation()

const receipts = ref([])
const loading = ref(false)
const error = ref(null)
const pagination = ref(null)
const filtersExpanded = ref(true)

const filters = ref({
  teamId: 'all',
  status: 'completed', 
  paymentStatus: '',
  templateId: '',
  dateFrom: '',
  dateTo: '',
  search: '',
  viewType: 'own', 
  page: 1,
  limit: 10
})


const teamOptions = computed(() => {
  const options = [
    { value: 'all', label: 'Alle Quittungen' },
    { value: 'personal', label: 'Persönliche Quittungen' }
  ]
  
  teams.value.forEach(team => {
    options.push({
      value: team.id,
      label: team.name
    })
  })
  
  return options
})


const statusOptions = computed(() => [
  { value: 'completed', label: 'Aktive' },
  { value: '', label: 'Alle' },
  { value: 'rejected', label: 'Abgelehnt' },
  { value: 'canceled', label: 'Storniert' }
])


const paymentStatusOptions = computed(() => [
  { value: '', label: 'Alle' },
  { value: 'open', label: 'Offen' },
  { value: 'payed', label: 'Bezahlt' },
  { value: 'planed', label: 'Geplant' }
])


const selectedTeam = ref({ value: 'all', label: 'Alle Quittungen' })
const selectedStatus = ref({ value: 'completed', label: 'Aktive' })
const selectedPaymentStatus = ref({ value: '', label: 'Alle' })
const selectedTemplate = ref(null)

const availableTemplates = ref([])
const templateLabelMap = ref({ '': 'Alle Designs', 'null': 'Ohne Team-Design' })

const computeTemplateLabel = (template) => {
  if (!template) {
    return 'Design'
  }

  if (template.id === null || template.id === undefined) {
    return 'Ohne Team-Design'
  }

  const rawLabel = template.customName || template.label || template.templateDisplayName || template.templateName || 'Design'
  return typeof rawLabel === 'string' ? rawLabel.trim() || 'Design' : 'Design'
}

const buildTemplateGroups = (templates = []) => {
  const groupsByLabel = new Map()

  templates.forEach(template => {
    if (!template) {
      return
    }

    const id = template.id === null || template.id === undefined ? 'null' : String(template.id)
    const label = computeTemplateLabel(template)
    const normalizedKey = typeof label === 'string' ? label.trim().toLowerCase() : 'design'

    if (!groupsByLabel.has(normalizedKey)) {
      groupsByLabel.set(normalizedKey, {
        label: label || 'Design',
        ids: new Set(),
        includeNull: false,
      })
    }

    const group = groupsByLabel.get(normalizedKey)
    if (!group.label && label) {
      group.label = label
    }
    if (id === 'null') {
      group.includeNull = true
    } else {
      group.ids.add(id)
    }
  })

  return Array.from(groupsByLabel.values()).map(group => {
    const idList = Array.from(group.ids).sort()
    const values = [...idList]
    if (group.includeNull) {
      values.unshift('null')
    }
    const value = values.join(',') || (group.includeNull ? 'null' : '')

    return {
      label: group.label,
      value,
      ids: idList,
      includeNull: group.includeNull
    }
  }).filter(group => group.value !== '')
}

const templateFilterOptions = computed(() => {
  const templates = Array.isArray(availableTemplates.value) ? availableTemplates.value : []
  const groups = buildTemplateGroups(templates)

  const sortedGroups = groups.sort((a, b) => (a.label || '').localeCompare(b.label || '', 'de', { sensitivity: 'base' }))

  const options = [{ value: '', label: 'Alle Designs' }]

  sortedGroups.forEach(group => {
    options.push({ value: group.value, label: group.label })
  })

  const currentValue = filters.value.templateId || ''
  if (currentValue && !options.some(option => option.value === currentValue)) {
    const labels = templateLabelMap.value || {}
    const fallbackLabel = labels[currentValue] || 'Ausgewähltes Design'
    options.push({ value: currentValue, label: fallbackLabel })
  }

  return options
})


const isTeamAdmin = (teamId) => {
  if (!teamId || !authStore.user?.roles) {
    return false
  }
  
  const userRole = authStore.user.roles.find(role => {

    const roleTeamId = role.teamId || role.team?.id
    return roleTeamId === teamId
  })
  
  return userRole?.role === 'teamAdmin'
}

const toggleFilters = () => {
  filtersExpanded.value = !filtersExpanded.value
}

const updateUrlQuery = () => {
  const query = { ...filters.value }
  

  Object.keys(query).forEach(key => {
    if (!query[key] || query[key] === '') {
      delete query[key]
    }
  })
  

  if (query.page === 1) delete query.page
  if (query.limit === 10) delete query.limit

  //if (query.viewType === 'own' && query.teamId !== 'all') delete query.viewType 
  if (query.status === 'completed') delete query.status 
  

  if (route.query.from) query.from = route.query.from
  
  router.replace({ query })
}

const updateTemplateLabels = (templates = []) => {
  if (!Array.isArray(templates)) {
    templateLabelMap.value = { '': 'Alle Designs', 'null': 'Ohne Team-Design' }
    return
  }

  const entries = { '': 'Alle Designs' }
  const groups = buildTemplateGroups(templates)

  if (groups.length === 0) {
    entries['null'] = 'Ohne Team-Design'
  }

  groups.forEach(group => {
    entries[group.value] = group.includeNull && group.value === 'null'
      ? 'Ohne Team-Design'
      : group.label
    group.ids.forEach(id => {
      entries[id] = group.label
    })
    if (group.includeNull) {
      entries['null'] = 'Ohne Team-Design'
    }
  })

  if (!entries['null']) {
    entries['null'] = 'Ohne Team-Design'
  }

  templateLabelMap.value = entries
}

const syncSelectedTemplate = () => {
  const options = templateFilterOptions.value || []
  if (options.length === 0) {
    selectedTemplate.value = null
    return
  }

  const currentValue = filters.value.templateId || ''
  let match = options.find(option => option.value === currentValue)

  if (!match && currentValue) {
    const templates = Array.isArray(availableTemplates.value) ? availableTemplates.value : []
    const groups = buildTemplateGroups(templates)
    const containingGroup = groups.find(group => group.value === currentValue || group.ids.includes(currentValue))

    if (containingGroup) {
      const groupValue = containingGroup.value
      if (groupValue && groupValue !== currentValue) {
        filters.value.templateId = groupValue
        updateUrlQuery()
        match = options.find(option => option.value === groupValue)
      }
    }
  }

  if (!match) {
    match = options[0]
    const fallbackValue = match?.value || ''
    const needsUpdate = filters.value.templateId !== fallbackValue
    filters.value.templateId = fallbackValue
    if (needsUpdate) {
      updateUrlQuery()
    }
  }

  selectedTemplate.value = match
}

syncSelectedTemplate()


const handleGoBackToTeam = () => {
  const teamId = route.query.teamId || filters.value.teamId
  goBackToTeam(teamId)
}

const onTeamFilterChange = (selectedOption) => {
  filters.value.teamId = selectedOption.value
  filters.value.templateId = ''
  syncSelectedTemplate()
  
  
  
  if (filters.value.teamId && filters.value.teamId !== 'all' && isTeamAdmin(filters.value.teamId)) {
    filters.value.viewType = undefined 
  } else {
    
    filters.value.viewType = 'own'
  }
  
  filters.value.page = 1
  updateUrlQuery()
  loadReceipts()
}

const onStatusFilterChange = (selectedOption) => {
  filters.value.status = selectedOption.value
  filters.value.page = 1
  updateUrlQuery()
  loadReceipts()
}

const onPaymentStatusFilterChange = (selectedOption) => {
  filters.value.paymentStatus = selectedOption.value
  filters.value.page = 1
  updateUrlQuery()
  loadReceipts()
}

const onTemplateFilterChange = (selectedOption) => {
  filters.value.templateId = selectedOption?.value || ''
  filters.value.page = 1
  updateUrlQuery()
  loadReceipts()
}

const togglePersonalView = (event) => {
  filters.value.viewType = event.target.checked ? 'own' : undefined
  filters.value.page = 1
  updateUrlQuery()
  loadReceipts()
}


let searchTimeout = null
const onSearchInput = () => {

  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    filters.value.page = 1
    updateUrlQuery()
    loadReceipts()
  }, 500)
}

const clearSearch = () => {
  filters.value.search = ''
  filters.value.page = 1
  updateUrlQuery()
  loadReceipts()
}


const loadReceipts = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = { ...filters.value }
    
    
    if (params.viewType !== 'own') {
      delete params.viewType
    }

    if (params.teamId === 'all') {
      delete params.teamId
    }
    

    if (filters.value.teamId === '') {
      params.teamId = 'null' 
    }
    
    
    if (params.status === 'active') {
      delete params.status
      
      params.excludeStatus = ['rejected', 'canceled']
    }
    

    Object.keys(params).forEach(key => {
      if (!params[key] && params[key] !== 'null') delete params[key]
    })

    let result

    if (filters.value.teamId && filters.value.teamId !== 'all' && isTeamAdmin(filters.value.teamId)) {
      
      result = await receiptsApi.getTeamReceipts(filters.value.teamId, params)
    } else {
      
      result = await receiptsApi.getUserReceipts(params)
    }
    
    receipts.value = result.receipts || []
    pagination.value = result.pagination
    availableTemplates.value = Array.isArray(result.templates) ? result.templates : []
    updateTemplateLabels(availableTemplates.value)
    syncSelectedTemplate()
  } catch (err) {
    error.value = err.response?.data?.error || 'Fehler beim Laden der Quittungen'
    console.error('Error loading receipts:', err)
    receipts.value = []
    pagination.value = null
  } finally {
    loading.value = false
  }
}


const viewReceipt = (receiptId) => {
  router.push(`/receipts/${receiptId}`)
}


const downloadPDF = async (receiptId) => {
  try {
    await receiptsApi.downloadReceiptPDF(receiptId)
    showSuccess('PDF wird heruntergeladen')
  } catch (err) {
    showError(err.response?.data?.error || 'Fehler beim Herunterladen der PDF')
  }
}

const recreateReceipt = (receipt) => {
  router.push({
    name: 'receipt-create',
    query: { sourceReceiptId: receipt.id },
  })
}


const deleteReceipt = async (receipt) => {
  const result = await Swal.fire({
    title: 'Quittung löschen?',
    text: `Möchtest du die Quittung "${receipt.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Ja, löschen',
    cancelButtonText: 'Abbrechen'
  })

  if (result.isConfirmed) {
    try {
      await receiptsApi.deleteReceipt(receipt.id)
      showSuccess('Quittung erfolgreich gelöscht')
      await loadReceipts()
    } catch (err) {
      showError(err.response?.data?.error || 'Fehler beim Löschen der Quittung')
    }
  }
}


const previousPage = () => {
  if (pagination.value.page > 1) {
    filters.value.page = pagination.value.page - 1
    updateUrlQuery()
    loadReceipts()
  }
}

const nextPage = () => {
  if (pagination.value.page < pagination.value.totalPages) {
    filters.value.page = pagination.value.page + 1
    updateUrlQuery()
    loadReceipts()
  }
}


const formatAmount = (amount) => {
  return parseFloat(amount).toFixed(2)
}

const formatDate = (dateString) => {
  if (!dateString) {
    return '-'
  }
  const parsed = new Date(dateString)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }
  return parsed.toLocaleDateString('de-DE')
}

const formatReceiptResolvedDate = (receipt) => {
  const value = getReceiptDateValue(receipt)
  if (!value) {
    return '-'
  }
  return formatDate(value)
}

const getReceiptDateTooltip = (receipt) => {
  const details = resolveReceiptDateDetails(receipt)
  return details.tooltip || undefined
}

const getReceiptDateSource = (receipt) => {
  const details = resolveReceiptDateDetails(receipt)
  return details.source === 'field' ? details.sourceLabel : null
}

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Entwurf',
    pending: 'Ausstehend',
    submitted: 'Eingereicht',
    approved: 'Genehmigt',
    rejected: 'Abgelehnt',
    canceled: 'Storniert',
    completed: 'Aktiv'
  }
  return labels[status] || status
}

const getPaymentStatusLabel = (paymentStatus) => {
  const labels = {
    open: 'Offen',
    payed: 'Bezahlt',
    planed: 'Vorbereitet'
  }
  return labels[paymentStatus] || paymentStatus
}

const getTeamName = (teamId) => {
  if (!teamId) return 'Privat'
  const team = teams.value.find(t => t.id === teamId)
  return team?.name || 'Unbekanntes Team'
}

const showCreatedBy = (receipt) => {

  return filters.value.teamId && !filters.value.viewType && isTeamAdmin(filters.value.teamId)
}

const getCreatorName = (receipt) => {
  if (receipt.user) {
    return receipt.user.firstName && receipt.user.lastName 
      ? `${receipt.user.firstName} ${receipt.user.lastName}`
      : receipt.user.email
  }
  if(receipt.userFirstName && receipt.userLastName) {
    return `${receipt.userFirstName} ${receipt.userLastName}`
  }
  return 'Unbekannt'
}

const getTemplateName = (receipt) => {
  
  if (receipt.teamTemplateId && receipt.template) {
    return receipt.template.customName || receipt.template.template?.displayName || receipt.template.template?.name || 'Standard'
  }
  
  
  if (receipt.template) {
    return receipt.template.customName || receipt.template.template?.displayName || receipt.template.template?.name || 'Standard'
  }
  
  
  return 'Standard'
}

const getExportInfoText = () => {
  const total = pagination.value?.total || 0
  let scope = 'Alle Ihre Quittungen'
  
  if (filters.value.teamId === 'all') {
    scope = 'Alle Ihre Quittungen (persönlich + Teams)'
  } else if (filters.value.teamId && !filters.value.viewType && isTeamAdmin(filters.value.teamId)) {
    const teamName = getTeamName(filters.value.teamId)
    scope = `Alle Quittungen von ${teamName} (Admin-Ansicht)`
  } else if (filters.value.teamId) {
    const teamName = getTeamName(filters.value.teamId)
    scope = `Ihre Quittungen in ${teamName}`
  }
  
  const activeFilters = []
  if (filters.value.status) activeFilters.push(`Status: ${getStatusLabel(filters.value.status)}`)
  if (filters.value.paymentStatus) activeFilters.push(`Bezahlstatus: ${getPaymentStatusLabel(filters.value.paymentStatus)}`)
  if (filters.value.templateId) {
    const templateOption = templateFilterOptions.value.find(option => option.value === filters.value.templateId)
    if (templateOption) {
      activeFilters.push(`Design: ${templateOption.label}`)
    }
  }
  if (filters.value.search) activeFilters.push(`Suche: "${filters.value.search}"`)
  if (filters.value.dateFrom) activeFilters.push(`Ab: ${formatDate(filters.value.dateFrom)}`)
  if (filters.value.dateTo) activeFilters.push(`Bis: ${formatDate(filters.value.dateTo)}`)
  
  let text = `${scope} (${total} Quittung${total !== 1 ? 'en' : ''})`
  if (activeFilters.length > 0) {
    text += ` mit aktiven Filtern: ${activeFilters.join(', ')}`
  }
  text += ' werden exportiert.'
  
  return text
}


const exportToExcel = async () => {
  try {
    loading.value = true
    

    const exportParams = { ...filters.value }
    delete exportParams.page
    delete exportParams.limit
    
    
    if (exportParams.viewType !== 'own') {
      delete exportParams.viewType
    }

    if (filters.value.teamId && !filters.value.viewType && isTeamAdmin(filters.value.teamId)) {
      exportParams.teamId = filters.value.teamId
    } else if (filters.value.teamId === 'all') {
      delete exportParams.teamId
    }
    
    
    if (exportParams.status === 'active') {
      delete exportParams.status
      exportParams.excludeStatus = ['rejected', 'canceled']
    }
    

    Object.keys(exportParams).forEach(key => {
      if (!exportParams[key]) delete exportParams[key]
    })
    
    const response = await receiptsApi.exportReceiptsExcel(exportParams)
    const blob = new Blob([response], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `quittungen-export-${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    showSuccess(`Excel-Export wurde heruntergeladen. ${getExportInfoText()}`)
  } catch (error) {
    console.error('Excel Export Fehler:', error)
    showError('Excel-Export fehlgeschlagen')
  } finally {
    loading.value = false
  }
}

const exportToZip = async () => {
  try {
    loading.value = true
    

    const exportParams = { ...filters.value }
    delete exportParams.page
    delete exportParams.limit
    
    
    if (exportParams.viewType !== 'own') {
      delete exportParams.viewType
    }

    if (filters.value.teamId && !filters.value.viewType && isTeamAdmin(filters.value.teamId)) {
      exportParams.teamId = filters.value.teamId
    } else if (filters.value.teamId === 'all') {
      delete exportParams.teamId
    }
    
    
    if (exportParams.status === 'active') {
      delete exportParams.status
      exportParams.excludeStatus = ['rejected', 'canceled']
    }
    

    Object.keys(exportParams).forEach(key => {
      if (!exportParams[key]) delete exportParams[key]
    })
    
    const response = await receiptsApi.exportReceiptsZip(exportParams)
    const blob = new Blob([response], { type: 'application/zip' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `quittungen-archiv-${new Date().toISOString().split('T')[0]}.zip`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    showSuccess(`ZIP-Export wurde heruntergeladen. ${getExportInfoText()}`)
  } catch (error) {
    console.error('ZIP Export Fehler:', error)
    showError('ZIP-Export fehlgeschlagen')
  } finally {
    loading.value = false
  }
}


const initializeFiltersFromQuery = () => {
  const query = route.query
  

  if (query.teamId) {
    filters.value.teamId = query.teamId
    
    const teamOption = teamOptions.value.find(option => option.value === query.teamId)
    if (teamOption) {
      selectedTeam.value = teamOption
    }
  }
  

  if (query.viewType) {
    filters.value.viewType = query.viewType
  } else {
    
    filters.value.viewType = 'own'
  }
  

  if (query.status) {
    filters.value.status = query.status
    const statusOption = statusOptions.value.find(option => option.value === query.status)
    if (statusOption) {
      selectedStatus.value = statusOption
    }
  } else {
    
    filters.value.status = 'completed'
  }
  
  if (query.paymentStatus) {
    filters.value.paymentStatus = query.paymentStatus
    const paymentOption = paymentStatusOptions.value.find(option => option.value === query.paymentStatus)
    if (paymentOption) {
      selectedPaymentStatus.value = paymentOption
    }
  }
  
  if (query.templateId) {
    filters.value.templateId = query.templateId
  } else {
    filters.value.templateId = ''
  }
  
  if (query.dateFrom) filters.value.dateFrom = query.dateFrom
  if (query.dateTo) filters.value.dateTo = query.dateTo
  if (query.search) filters.value.search = query.search
  if (query.page) filters.value.page = parseInt(query.page) || 1
  if (query.limit) filters.value.limit = parseInt(query.limit) || 10

  syncSelectedTemplate()
}

onMounted(async () => {

  if (authStore.isAuthenticated) {
    await authStore.updateProfile()
  }

  await teamsStore.fetchTeams()
  

  initializeFiltersFromQuery()
  
  
  if (!route.query.viewType) {
    if (filters.value.teamId && filters.value.teamId !== 'all' && isTeamAdmin(filters.value.teamId)) {
      filters.value.viewType = undefined
    } else {
      filters.value.viewType = 'own'
    }
  }

  
  const teamOption = teamOptions.value.find(option => option.value === filters.value.teamId)
  if (teamOption) {
    selectedTeam.value = teamOption
  }
  
  const statusOption = statusOptions.value.find(option => option.value === filters.value.status)
  if (statusOption) {
    selectedStatus.value = statusOption
  }
  
  const paymentOption = paymentStatusOptions.value.find(option => option.value === filters.value.paymentStatus)
  if (paymentOption) {
    selectedPaymentStatus.value = paymentOption
  }

  loadReceipts()
})
</script>

<style scoped>
/* Vue Multiselect Styles */

.receipt-card {
  transition: all 0.2s ease;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  cursor: pointer;
}

.receipt-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  border-color: #007bff;
}

.amount-display {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.detail-item {
  margin-bottom: 0.75rem;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.detail-value {
  font-size: 0.9rem;
  color: #212529;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.badge {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}




.card-title {
  color: #2c3e50;
  font-size: 1rem;
}


/* Layout adjustments */
.receipt-card .card-body {
  padding: 1rem 1.25rem !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .receipt-card .card-body {
    padding: 0.75rem 1rem !important;
  }
  
  /* Mobile button improvements */
  .btn-sm {
    padding: 0.375rem 0.5rem !important;
    font-size: 0.8rem !important;
    min-width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Action buttons in cards */
  .receipt-card .action-buttons .btn-sm {
    min-width: 40px;
    height: 40px;
    padding: 0.5rem !important;
    flex-shrink: 0;
  }
  
  /* Ensure action buttons don't wrap on mobile */
  .receipt-card .action-buttons {
    flex-wrap: nowrap;
    min-width: 0;
  }
  
  /* Header action buttons */
  .header-actions .btn {
    padding: 0.5rem 0.75rem !important;
    font-size: 0.875rem !important;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  /* Make header buttons stack on very small screens */
  @media (max-width: 576px) {
    .header-actions {
      flex-direction: column;
      gap: 0.5rem !important;
      width: 100%;
    }
    
    .header-actions .btn {
      width: 100%;
      justify-content: center;
    }
  }
  
  .card-title {
    font-size: 0.9rem;
  }
}

/* Animation for loading state */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.receipt-card {
  animation: fadeInUp 0.3s ease-out;
}

/* Multiselect Custom Styles */
:deep(.multiselect) {
  min-height: 38px;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  position: relative !important;
}

:deep(.multiselect__tags) {
  min-height: 38px;
  padding: 6px 40px 0 8px;
  border: none;
  border-radius: 0.375rem;
  background: #fff;
}

:deep(.multiselect__placeholder) {
  color: #6c757d;
  font-size: 0.875rem;
  margin-bottom: 0;
  padding-top: 0;
}

:deep(.multiselect__single) {
  background: transparent;
  color: #212529;
  font-size: 0.875rem;
  margin-bottom: 0;
  padding: 0;
  line-height: 1.5;
}

:deep(.multiselect__input) {
  background: transparent;
  color: #212529;
  font-size: 0.875rem;
  margin-bottom: 0;
  padding: 0;
  line-height: 1.5;
}

:deep(.multiselect__input::placeholder) {
  color: #6c757d;
}

:deep(.multiselect__select) {
  height: 36px;
  right: 1px;
  top: 1px;
}

:deep(.multiselect__select:before) {
  border-color: #6c757d transparent transparent;
  border-style: solid;
  border-width: 5px 5px 0;
  content: "";
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

:deep(.multiselect__content-wrapper) {
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

:deep(.multiselect__content) {
  z-index: 9999 !important;
  position: relative;
}

:deep(.multiselect__option) {
  padding: 8px 12px;
  font-size: 0.875rem;
  color: #212529;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
}

:deep(.multiselect__option:hover) {
  background: var(--bs-primary);
  color: #fff;
}

:deep(.multiselect__option--highlight) {
  background: var(--bs-primary);
  color: #fff;
}

:deep(.multiselect__option--selected) {
  background: var(--bs-light);
  color: var(--bs-dark);
  font-weight: 600;
}

:deep(.multiselect__option--selected:hover) {
  background: var(--bs-primary);
  color: #fff;
}

:deep(.multiselect__option--highlight.multiselect__option--selected) {
  background: var(--bs-primary);
  color: #fff;
}

/* Focus States */
:deep(.multiselect:focus-within) {
  border-color: var(--bs-primary);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
}

/* Hover States */
:deep(.multiselect:hover .multiselect__tags) {
  border-color: var(--bs-primary) !important;
}

:deep(.multiselect__option--highlight){
  background: var(--bs-primary) !important;
}
</style>
